import { PaintColor } from "@omni/types/api/paintColors/bff/paintColor.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { Mapped } from "@omni/utils/context/mapped.context";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use } from "react";

type FamiliesContextValue = {
    families: Map<string, Array<PaintColor>>;
};

type FamiliesContextProviderProps = RequireChildrenFor<{
    colors: Array<PaintColor>;
}>;

export const FamiliesContext = createContext<FamiliesContextValue | undefined>(
    undefined
);
FamiliesContext.displayName = "FamiliesContext";

export const FamiliesContextProvider = ({
    colors,
    children
}: FamiliesContextProviderProps) => {
    const groupedByFamily = colors.reduce((acc, color) => {
        if (!acc.has(color.family.id)) {
            acc.set(color.family.id, []);
        }
        acc.get(color.family.id)?.push(color);
        return acc;
    }, new Map<string, Array<PaintColor>>());

    return (
        <FamiliesContext
            value={{
                families: groupedByFamily
            }}
        >
            <Mapped array={[...groupedByFamily.keys()]}>{children}</Mapped>
        </FamiliesContext>
    );
};

export const useFamiliesContext = () => {
    const context = use<FamiliesContextValue | undefined>(FamiliesContext);

    if (!isDefined(context)) {
        throw new Error(`${FamiliesContext.displayName} is undefined.`);
    }

    return context;
};
